import React, { useEffect, useRef, useState } from "react";
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import { AlertBell24 } from "@bphxd/ds-core-react/lib/icons";
import { getTaxNotification } from "../../api/TAX/getTax";
import { useMsal } from "@azure/msal-react";

export default function Notification() {
  const { accounts } = useMsal();
  const token = sessionStorage.getItem("jwt");
  const role = sessionStorage.getItem("role");

  const hasFetched = useRef(false);
  const userEmail = accounts?.[0]?.username || "";
  
  const [notificationCount, setNotificationCount] = useState(0);
  interface Notification {
    tax_id: string;
    cust_name: string;
    order_number: string;
    vessel_name: string;
    vessel_imo_number: string;
    port_name: string;
    country_name: string;
    tax_type: string;
    status: string;
    created_date: string;
    expiry_date: string;
    document_link?: string;
  }

  const [notificationData, setNotificationData] = useState<Notification[]>([]);
  const [readNotifications, setReadNotifications] = useState<string[]>([]);
  const [selectedNotifications, setSelectedNotifications] = useState<Set<string>>(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (userEmail) {
      console.log("Fetching notifications for:", userEmail);
      fetchNotifications();
      
      // Refresh every 5 minutes
      const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [userEmail]);

  const fetchNotifications = async () => {
    try {
      setIsLoading(true);
      console.log("Fetching notifications...", new Date().toLocaleTimeString());
      const res = await getTaxNotification({
        offSet: "0"
      },token);
      
      if (res.body?.success) {
        const count = res.body?.totalPending || 0;
        const documents = res.body?.documents || [];
        
        setNotificationCount(count);
        setNotificationData(documents);
        
        // Store in sessionStorage for backup
        sessionStorage.setItem("notificationcount", count);
        sessionStorage.setItem("notificationData", JSON.stringify(documents));
        
        console.log("✅ Fetched Tax Notifications:", res);
      }
    } catch (error) {
      console.error("❌ Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const refireNotifications = () => {
    // Clear read notifications to show all as new
    setReadNotifications([]);
    // Refetch from backend
    fetchNotifications();
  };

  const handleNotificationClick = (taxId: string) => {
    setReadNotifications(prev => {
      if (prev.includes(taxId)) {
        // Mark as unread - remove from read list
        console.log("📖 Marking as unread:", taxId);
        return prev.filter(id => id !== taxId);
      } else {
        // Mark as read - add to read list
        console.log("✓ Marking as read:", taxId);
        return [...prev, taxId];
      }
    });
  };

  const handleCheckboxChange = (taxId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setSelectedNotifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taxId)) {
        newSet.delete(taxId);
      } else {
        newSet.add(taxId);
      }
      return newSet;
    });
  };

  const markSelectedAsRead = () => {
    if (selectedNotifications.size > 0) {
      setReadNotifications(prev => [
        ...prev,
        ...Array.from(selectedNotifications).filter(id => !prev.includes(id))
      ]);
      setSelectedNotifications(new Set());
      console.log("✓ Marked selected as read");
    }
  };

  const markSelectedAsUnread = () => {
    if (selectedNotifications.size > 0) {
      setReadNotifications(prev =>
        prev.filter(id => !selectedNotifications.has(id))
      );
      setSelectedNotifications(new Set());
      console.log(" Marked selected as unread");
    }
  };

  const toggleAllRead = () => {
    if (unreadCount > 0) {
      // Mark all as read
      const allTaxIds = notificationData.map(doc => doc.tax_id);
      setReadNotifications(allTaxIds);
      setSelectedNotifications(new Set());
      console.log("✓ Marked all as read");
    } else {
      // Mark all as unread
      setReadNotifications([]);
      setSelectedNotifications(new Set());
      console.log("📖 Marked all as unread");
    }
  };

  const unreadCount = notificationCount - readNotifications.length;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <UncontrolledDropdown>
        <DropdownToggle
          tag="div"
          style={{ cursor: 'pointer', position: 'relative', display: 'inline-block' }}
          caret={false}
        >
          <AlertBell24/>
          {unreadCount > 0 && (
            <span
              style={{
                position: 'absolute',
                top: '-5px',
                right: '-5px',
                background: 'red',
                color: 'white',
                borderRadius: '50%',
                padding: '2px 6px',
                fontSize: '10px',
                fontWeight: 'bold',
                minWidth: 'auto',
                textAlign: 'center'
              }}
            >
              {unreadCount}
            </span>
          )}
        </DropdownToggle>

        <DropdownMenu right style={{ minWidth: '400px', maxWidth: '450px', maxHeight: '400px', overflowY: 'auto', overflowX: 'hidden' }}>
          <DropdownItem header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span>Notifications ({unreadCount} Unread)</span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {selectedNotifications.size > 0 && (
                <>
                  <button 
                    onClick={markSelectedAsRead}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#4caf50',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      padding: '0',
                      whiteSpace: 'nowrap'
                    }}
                    title="Mark selected as read"
                  >
                    ✓ Read ({selectedNotifications.size})
                  </button>
                  <button 
                    onClick={markSelectedAsUnread}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ff9800',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      padding: '0',
                      whiteSpace: 'nowrap'
                    }}
                    title="Mark selected as unread"
                  >
                    ↩ Unread
                  </button>
                </>
              )}
              <button 
                onClick={toggleAllRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1976d2',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  padding: '0',
                  whiteSpace: 'nowrap'
                }}
                title={unreadCount > 0 ? 'Mark all as read' : 'Mark all as unread'}
              >
                {unreadCount > 0 ? 'Mark All Read' : 'Mark All Unread'}
              </button>
              {isLoading && <span style={{ fontSize: '12px', color: '#999' }}>⟳</span>}
            </div>
          </DropdownItem>
          <DropdownItem divider />

          {notificationData.length > 0 ? (
            notificationData.map((doc) => (
              <DropdownItem
                key={doc.tax_id}
                onClick={() => handleNotificationClick(doc.tax_id)}
                style={{
                  backgroundColor: readNotifications.includes(doc.tax_id) ? '#f5f5f5' : '#fff9e6',
                  borderLeft: readNotifications.includes(doc.tax_id) ? 'none' : '4px solid #ff9800',
                  padding: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '8px' }}>
                  {/* <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                    <input
                      type="checkbox"
                      checked={selectedNotifications.has(doc.tax_id)}
                      onChange={(e) => handleCheckboxChange(doc.tax_id, e)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                    />
                    <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.cust_name}</strong>
                  </div> */}
                  <span
                    style={{
                      color: readNotifications.includes(doc.tax_id) ? '#999' : '#ff9800',
                      fontSize: '16px',
                      marginLeft: '8px',
                      flexShrink: 0
                    }}
                    title={readNotifications.includes(doc.tax_id) ? 'Read' : 'Unread'}
                  >
                    {readNotifications.includes(doc.tax_id) ? '☐' : '●'}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#1976d2', marginBottom: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Order: {doc.order_number} | Vessel: {doc.vessel_name} ({doc.vessel_imo_number}) | Port: {doc.port_name}, {doc.country_name} | Type: {doc.tax_type} | Status: {doc.status} | Created: {new Date(doc.created_date).toLocaleDateString()} | Expiry: {new Date(doc.expiry_date).toLocaleDateString()}
                </div>
              </DropdownItem>
            ))
          ) : (
            <DropdownItem>
              <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>
                {isLoading ? 'Loading notifications...' : 'No pending notifications'}
              </div>
            </DropdownItem>
          )}
        </DropdownMenu>
      </UncontrolledDropdown>
    </div>
  );
}